import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ShoppingBasket, Loader2, RotateCcw, CheckCircle2, Clock, Truck, Home as HomeIcon, XCircle, Package, Lock, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { useCart } from '@/context/CartContext';
import { getOrder, getProduct } from '@/lib/api';
import { readOrderHistory } from '@/lib/orderHistory';
import { toast } from 'sonner';

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Confirmed', icon: CheckCircle2, color: 'bg-blue-100 text-blue-800' },
  preparing: { label: 'Preparing', icon: Package, color: 'bg-purple-100 text-purple-800' },
  out_for_delivery: { label: 'Out for delivery', icon: Truck, color: 'bg-orange-100 text-orange-800' },
  delivered: { label: 'Delivered', icon: HomeIcon, color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function OrderCard({ order, onReorder, reorderingId }) {
  const status = STATUS_META[order.status] || STATUS_META.pending;
  const StatusIcon = status.icon;
  const firstTwo = order.items.slice(0, 2).map((i) => i.name).join(', ');
  const moreCount = Math.max(order.items.length - 2, 0);

  return (
    <div
      className="bg-white rounded-xl border border-border p-4 flex flex-col gap-3"
      data-testid={`order-card-${order.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-base">#{order.order_number}</p>
          <p className="text-xs text-muted-foreground">{formatRelative(order.created_at)}</p>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${status.color}`}
          data-testid={`order-status-${order.id}`}
        >
          <StatusIcon className="w-3.5 h-3.5" />
          {status.label}
        </span>
      </div>

      <div className="text-sm text-muted-foreground line-clamp-2">
        {firstTwo}{moreCount > 0 ? ` +${moreCount} more` : ''}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-border">
        <div>
          <p className="text-xs text-muted-foreground">{order.items.length} item{order.items.length === 1 ? '' : 's'}</p>
          <p className="font-extrabold text-primary text-lg">₹{order.total.toFixed(0)}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReorder(order)}
            disabled={reorderingId === order.id}
            className="rounded-full h-10 px-4 font-semibold"
            data-testid={`reorder-btn-${order.id}`}
          >
            {reorderingId === order.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Reorder
              </>
            )}
          </Button>
          <Link to={`/order-confirmation/${order.id}`} data-testid={`view-order-${order.id}`}>
            <Button size="sm" className="rounded-full h-10 px-4 font-semibold">View</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function MyOrdersPage() {
  const { addToCart, clearCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderingId, setReorderingId] = useState(null);

  // Load orders from local history (each entry carries its own view_token)
  useEffect(() => {
    const cached = readOrderHistory();

    const loadEntries = async () => {
      if (cached.entries.length === 0) {
        setLoading(false);
        return;
      }
      try {
        const results = await Promise.allSettled(
          cached.entries.map((e) => getOrder(e.id, e.token))
        );
        const fetched = results
          .filter((r) => r.status === 'fulfilled')
          .map((r) => r.value.data)
          // Keep only orders that returned items (redacted endpoint still returns them)
          .filter((o) => Array.isArray(o.items));
        fetched.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(fetched);
      } catch (err) {
        console.error('Load orders error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEntries();
  }, []);

  const handleReorder = async (order) => {
    setReorderingId(order.id);
    try {
      const results = await Promise.allSettled(
        order.items.map((i) => getProduct(i.product_id))
      );
      const available = results
        .filter((r) => r.status === 'fulfilled' && r.value.data.in_stock)
        .map((r) => ({
          product: r.value.data,
          qty: order.items.find((i) => i.product_id === r.value.data.id)?.quantity || 1,
        }));

      if (available.length === 0) {
        toast.error('None of the items are currently available');
        return;
      }

      clearCart();
      available.forEach(({ product, qty }) => {
        for (let k = 0; k < qty; k += 1) addToCart(product);
      });
      const missing = order.items.length - available.length;
      toast.success(
        `Added ${available.length} item${available.length === 1 ? '' : 's'} to cart${missing > 0 ? ` (${missing} unavailable)` : ''}`
      );
    } catch (err) {
      console.error('Reorder error:', err);
      toast.error('Could not reorder');
    } finally {
      setReorderingId(null);
    }
  };

  const isEmpty = useMemo(() => !loading && orders.length === 0, [loading, orders]);

  return (
    <CustomerLayout>
      <div className="px-4 py-4 max-w-2xl mx-auto" data-testid="my-orders-page">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-xl md:text-2xl font-extrabold">My Orders</h1>
        </div>

        {/* Privacy reassurance */}
        <div
          className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-3 mb-4"
          data-testid="my-orders-privacy-banner"
        >
          <Lock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-foreground leading-relaxed">
            Only orders placed from <b>this device</b> are shown.
            Your details stay private — no one else can look up your orders.
          </p>
        </div>

        {/* Orders list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : isEmpty ? (
          <div className="text-center py-12 bg-white border border-border rounded-xl" data-testid="orders-empty-state">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <ShoppingBasket className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-bold text-lg mb-1">No orders yet</h3>
            <p className="text-sm text-muted-foreground mb-5 px-4">
              Once you place an order, you can track and reorder it from here.
            </p>
            <Link to="/products">
              <Button className="rounded-full h-12 px-6 font-bold" data-testid="orders-shop-btn">
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onReorder={handleReorder}
                reorderingId={reorderingId}
              />
            ))}
          </div>
        )}

        {/* Help: lost device / cross-device access */}
        {!loading && !isEmpty && (
          <div className="mt-6 bg-muted/50 border border-border rounded-xl p-3" data-testid="orders-help">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Switched phones or cleared your browser? Past orders are tied to the device they were placed from.
              {' '}
              <a
                href={`https://wa.me/919603540072?text=${encodeURIComponent('Hi QGT! I need help accessing my past orders.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-semibold inline-flex items-center gap-1"
                data-testid="orders-help-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Contact support on WhatsApp
              </a>
              {' '}and we&apos;ll help you recover them.
            </p>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}
